import { currentRegistries } from '@/shared/modules/store';
import { MY_NOTES_PATH, resolveActiveSubNav, resolveModuleFromPath } from './moduleNav';
import { NOTIFICATIONS_PATH } from './notifications/paths';

/**
 * Human label for the current page, resolved from the module's nav.
 *
 * The two personal surfaces are special-cased: they were hoisted OUT of
 * `MODULE_NAV` (2026-07-29), so a nav lookup finds nothing and the crumb
 * rendered as a bare module name — or, on the module-less `/my-notes`, as
 * the literal fallback "Amassa" with no page at all.
 */
export function pageLabel(pathname: string): { module?: string; page?: string } {
  if (pathname === MY_NOTES_PATH) {
    // Genuinely global — one /api/notes for every module. No module crumb
    // would be honest, but an empty left crumb looks broken, so the product
    // name stands in for "this belongs to you, not to a module".
    return { module: 'Amassa', page: 'My Notes' };
  }
  // The inbox is the app's, like My Notes (the bell in the top bar).
  if (pathname === NOTIFICATIONS_PATH) return { module: 'Amassa', page: 'Notifications' };
  const mod = resolveModuleFromPath(pathname);
  if (!mod) return {};
  // The tenant's labels and rail (Admin ▸ Modules).
  const { labels, nav, offRail } = currentRegistries();
  const moduleLabel = labels[mod];
  if (pathname === `/${mod}/hot-plate`) {
    return { module: moduleLabel, page: 'Hot Plate' };
  }
  if (pathname === `/${mod}/my-notes`) {
    // Legacy per-module deep link — the routes still exist.
    return { module: moduleLabel, page: 'My Notes' };
  }
  // Routes that render a named page but are not rail entries (the
  // region-filtered logs views). Without this the crumb on
  // /coportal/log-my read "CS/OPS Portal" and stopped — the page header
  // that used to carry the name was deleted on 2026-08-10.
  const extra = offRail[pathname];
  if (extra) return { module: moduleLabel, page: extra };
  // A destination's log page, `/coportal/log/<code>` (brief §8.4.4 G2):
  // named like the old off-rail pages ("MY logs").
  const dest = mod === 'coportal' ? /^\/coportal\/log\/([^/]+)\/?$/.exec(pathname) : null;
  if (dest) return { module: moduleLabel, page: `${decodeURIComponent(dest[1] ?? '').toUpperCase()} logs` };
  const activeTo = resolveActiveSubNav(mod, pathname);
  let page: string | undefined;
  if (activeTo) {
    for (const group of nav[mod] ?? []) {
      const hit = group.items.find((i) => i.to === activeTo);
      if (hit) {
        page = hit.label;
        break;
      }
    }
  }
  return { module: moduleLabel, page };
}
